import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, Heart, Search, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { api, money } from "@/lib/api";

const ANNOUNCEMENTS = [
  "End of Season Sale · Up to 70% Off Festive Styles",
  "Now Booking Bulk Bridesmaid Orders · 2026–27 Wedding Collection",
  "Same-Day Delivery via DoorDash · Within 25 miles of Schaumburg",
  "Based in Chicago Suburbs · Fast US Delivery",
  "Free U.S. shipping over $75",
  "★ 4.9 Google Rating · 10 Years in Business",
  "In-Stock & Ready to Ship from Schaumburg, IL",
  "New styles added daily",
];

const col = (slug) => `/shop?collection=${slug}`;

// Structured main menu (mapped to real Shopify collection handles)
const MENU = [
  { label: "Season Sale", to: "/festive-sale", diwali: true },
  { label: "Shop All", to: "/shop" },
  { label: "New Drops", to: col("new-arrivals") },
  {
    label: "Women",
    children: [
      { label: "Casual Kurtis", to: col("kurtis") },
      { label: "Anarkali", to: col("kurti") },
      { label: "Lehenga Choli", to: col("lehenga-choli") },
      { label: "Sharara Suit", to: col("sharara-suits") },
      { label: "Sarees", to: col("sarees") },
      { label: "Readymade Blouses", to: col("readymade-blouse") },
      { label: "Dupattas", to: col("dupatta") },
      { label: "Jewelry", to: col("jewelry-and-accessories") },
    ],
  },
  {
    label: "Men",
    children: [
      { label: "Kurta Pajama", to: col("men") },
      { label: "Sherwani", to: col("sherwani") },
    ],
  },
  {
    label: "Kids",
    children: [
      { label: "Boys", to: col("kids-boys") },
      { label: "Girls", to: col("kids-girls") },
    ],
  },
  { label: "Plus Size", to: col("plus-size-collection"), highlight: true },
  { label: "Bridesmaids", to: "/bridesmaid-dresses", highlight: true },
  { label: "Outfit Finder", to: "/outfit-finder", accent: true },
];

const tid = (s) => s.toLowerCase().replace(/\s+/g, "-");

const navClass = (n) => {
  const base = "text-[0.72rem] tracking-[0.16em] xl:tracking-[0.1em] min-[1400px]:tracking-[0.16em] uppercase font-body transition-colors duration-300 whitespace-nowrap";
  if (n.diwali) return `${base} bg-[#3D0A28] border border-[#F5C542]/60 text-[#F5C542] font-semibold rounded-full px-3 py-1 hover:bg-[#F5C542] hover:text-[#3D0A28]`;
  if (n.highlight) return `${base} border border-[#B84A3B] text-[#B84A3B] font-semibold rounded-full px-3 py-1 hover:bg-[#B84A3B] hover:text-white`;
  if (n.accent) return `${base} text-[#B84A3B] font-semibold hover:text-[#1A1A1A]`;
  return `${base} text-[#1A1A1A] hover:text-[#B84A3B]`;
};

const DesktopNavItem = ({ n }) => {
  if (!n.children) {
    return (
      <NavLink to={n.to} data-testid={`nav-${tid(n.label)}`} className={navClass(n)}>
        {n.label}
      </NavLink>
    );
  }
  return (
    <div className="relative group" data-testid={`nav-group-${tid(n.label)}`}>
      <button className={`${navClass(n)} inline-flex items-center gap-1`} data-testid={`nav-${tid(n.label)}`}>
        {n.label}
        <ChevronDown size={12} strokeWidth={1.8} className="transition-transform duration-200 group-hover:rotate-180" />
      </button>
      {/* pt-3 bridges the gap so the panel stays open while moving the cursor down */}
      <div className="absolute left-0 top-full pt-3 hidden group-hover:block z-50">
        <div className="min-w-[13rem] bg-[#FFFFFF] border border-[#E5E5DF] shadow-lg py-2" data-testid={`dropdown-${tid(n.label)}`}>
          {n.children.map((c) => (
            <Link key={c.label} to={c.to} data-testid={`nav-sub-${tid(c.label)}`}
              className="block px-4 py-2.5 text-[0.8rem] font-body text-[#1A1A1A] hover:bg-[#EFEFEA] hover:text-[#B84A3B] transition-colors whitespace-nowrap">
              {c.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

const MobileNavGroup = ({ n, onClose }) => {
  const [expanded, setExpanded] = useState(false);
  if (!n.children) {
    return (
      <Link to={n.to} onClick={onClose} data-testid={`mobile-nav-${tid(n.label)}`}
        className={`text-sm tracking-[0.12em] uppercase font-body ${n.accent || n.highlight || n.diwali ? "text-[#B84A3B] font-semibold" : "text-[#1A1A1A]"}`}>
        {n.label}
      </Link>
    );
  }
  return (
    <div>
      <button onClick={() => setExpanded((v) => !v)} data-testid={`mobile-nav-${tid(n.label)}`}
        className="w-full flex items-center justify-between text-sm tracking-[0.12em] uppercase font-body text-[#1A1A1A]">
        {n.label}
        <ChevronDown size={16} className={`transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
      </button>
      {expanded && (
        <div className="mt-3 ml-3 pl-3 border-l border-[#E5E5DF] flex flex-col gap-3" data-testid={`mobile-dropdown-${tid(n.label)}`}>
          {n.children.map((c) => (
            <Link key={c.label} to={c.to} onClick={onClose} data-testid={`mobile-nav-sub-${tid(c.label)}`}
              className="text-[0.8rem] tracking-[0.08em] font-body text-[#6E6E69] hover:text-[#B84A3B] transition-colors">
              {c.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export const Header = () => {
  const { count, setOpen } = useCart();
  const { count: wishCount } = useWishlist();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const closeSearch = () => { setSearchOpen(false); setTerm(""); setResults([]); };
  const submitSearch = (e) => {
    e.preventDefault();
    if (!term.trim()) return;
    navigate(`/shop?q=${encodeURIComponent(term.trim())}`);
    closeSearch();
  };

  useEffect(() => {
    if (!searchOpen) return;
    const t = term.trim();
    if (t.length < 2) { setResults([]); return; }
    const id = setTimeout(() => {
      api.get(`/products?q=${encodeURIComponent(t)}&limit=6`).then((r) => setResults(r.data)).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(id);
  }, [term, searchOpen]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [annIdx, setAnnIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setAnnIdx((i) => (i + 1) % ANNOUNCEMENTS.length), 3500);
    return () => clearInterval(id);
  }, []);

  const left = MENU.slice(0, 5);
  const right = MENU.slice(5);

  return (
    <>
      <div data-testid="announcement-bar"
        className="relative bg-[#1A1A1A] text-[#FFFFFF] h-9 overflow-hidden flex items-center justify-center px-4">
        <AnimatePresence mode="wait">
          <motion.span
            key={annIdx}
            initial={{ opacity: 0, y: 9 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -9 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            data-testid="announcement-message"
            className="text-[0.7rem] tracking-[0.2em] uppercase font-mono whitespace-nowrap"
          >
            {ANNOUNCEMENTS[annIdx]}
          </motion.span>
        </AnimatePresence>
      </div>

      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#FFFFFF]/80 border-b border-[#E5E5DF]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <button data-testid="mobile-menu-toggle" className="xl:hidden" onClick={() => setMobileOpen((v) => !v)} aria-label="Menu">
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          <nav className="hidden xl:flex items-center gap-4 min-[1400px]:gap-6 flex-1">
            {left.map((n) => (
              <DesktopNavItem key={n.label} n={n} />
            ))}
          </nav>

          <Link to="/" data-testid="logo-link" className="absolute left-1/2 -translate-x-1/2 text-center leading-none">
            <div className="font-display text-xl sm:text-[1.7rem] xl:text-[1.45rem] min-[1400px]:text-[1.7rem] tracking-tight whitespace-nowrap">Kurti Connection</div>
            <div className="overline text-[0.55rem] mt-0.5 text-[#B84A3B]">USA</div>
          </Link>

          <div className="hidden xl:flex items-center gap-4 min-[1400px]:gap-6 flex-1 justify-end">
            {right.map((n) => (
              <DesktopNavItem key={n.label} n={n} />
            ))}
          </div>

          <div className="flex items-center gap-3 sm:gap-4 ml-2 sm:ml-4">
            <button data-testid="search-toggle" onClick={() => setSearchOpen((v) => !v)} aria-label="Search"
              className="hover:text-[#B84A3B] transition-colors duration-300">
              <Search size={20} strokeWidth={1.5} />
            </button>
            <Link to="/wishlist" data-testid="wishlist-link" aria-label="Wishlist"
              className="relative hover:text-[#B84A3B] transition-colors duration-300">
              <Heart size={20} strokeWidth={1.5} />
              {wishCount > 0 && (
                <span data-testid="wishlist-count-badge"
                  className="absolute -top-2 -right-2 bg-[#B84A3B] text-white text-[0.6rem] font-semibold w-4 h-4 flex items-center justify-center rounded-full">
                  {wishCount}
                </span>
              )}
            </Link>
            <button data-testid="cart-button" onClick={() => setOpen(true)}
              className="relative hover:text-[#B84A3B] transition-colors duration-300" aria-label="Open cart">
              <ShoppingBag size={21} strokeWidth={1.5} />
              {count > 0 && (
                <span data-testid="cart-count-badge"
                  className="absolute -top-2 -right-2 bg-[#B84A3B] text-white text-[0.6rem] font-semibold w-4 h-4 flex items-center justify-center rounded-full">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="border-t border-[#E5E5DF] bg-[#FFFFFF]">
            <form onSubmit={submitSearch} data-testid="search-form"
              className="px-5 sm:px-8 py-3 flex items-center gap-3 max-w-[1400px] mx-auto">
              <Search size={18} className="text-[#6E6E69]" />
              <input autoFocus data-testid="search-input" value={term} onChange={(e) => setTerm(e.target.value)}
                placeholder="Search by name, colour or occasion…"
                className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-[#9a9a94]" />
              <button type="submit" className="text-[0.65rem] font-mono tracking-[0.18em] uppercase border-b border-[#1A1A1A] pb-0.5 hover:text-[#B84A3B] hover:border-[#B84A3B] transition-colors">Search</button>
              <button type="button" onClick={closeSearch} aria-label="Close search"><X size={18} className="text-[#6E6E69] hover:text-[#B84A3B]" /></button>
            </form>

            {results.length > 0 && (
              <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-4" data-testid="search-suggestions">
                <div className="border border-[#E5E5DF] divide-y divide-[#E5E5DF]">
                  {results.map((p) => (
                    <Link key={p.slug} to={`/product/${p.slug}`} onClick={closeSearch}
                      data-testid={`suggestion-${p.slug}`}
                      className="flex items-center gap-3 px-3 py-2.5 hover:bg-[#EFEFEA] transition-colors">
                      <img src={p.images?.[0]} alt={p.title} className="w-11 h-14 object-cover bg-[#EFEFEA]" />
                      <div className="flex-1 min-w-0">
                        <div className="font-body text-sm text-[#1A1A1A] truncate">{p.title}</div>
                        <div className="font-body text-xs text-[#6E6E69] mt-0.5">{money(p.price)}</div>
                      </div>
                    </Link>
                  ))}
                  <button onClick={submitSearch} data-testid="suggestion-see-all"
                    className="w-full text-center py-2.5 text-[0.62rem] font-mono tracking-[0.2em] uppercase text-[#B84A3B] hover:bg-[#EFEFEA] transition-colors">
                    See all results for “{term.trim()}”
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {mobileOpen && (
          <div className="xl:hidden border-t border-[#E5E5DF] bg-[#FFFFFF] px-5 py-4 flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
            {MENU.map((n) => (
              <MobileNavGroup key={n.label} n={n} onClose={() => setMobileOpen(false)} />
            ))}
          </div>
        )}
      </header>
    </>
  );
};
